import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { api } from '../utils/api';

export default function Minerados() {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('7d');
  const [nicho, setNicho] = useState('');
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);
  const [toast, setToast] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [formato, setFormato] = useState('');
  const [cloaker, setCloaker] = useState('');
  const [sort, setSort] = useState('mined_at');
  const searchTimer = useRef(null);
  const perPage = 24;

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 2000); };

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = { period, sort, order: 'desc', limit: perPage, page };
      if (nicho) params.nicho = nicho;
      if (search) params.search = search;
      if (formato) params.format = formato;
      if (cloaker === 'yes') params.has_cloaker = true;
      if (cloaker === 'no') params.has_cloaker = false;
      const adsData = await api.getAds(params);
      setAds(adsData.data || []); setTotal(adsData.total || 0);
    } catch { setError('Erro ao carregar dados.'); }
    setLoading(false);
  }, [period, nicho, search, page, formato, cloaker, sort]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [period, nicho, search, formato, cloaker, sort]);
  const nichos = useMemo(() => [...new Set(ads.map(a => a.nicho).filter(Boolean))], [ads]);
  const copyUrl = (url) => { navigator.clipboard.writeText(url); showToast('Copiado!'); };
  const totalPages = Math.ceil(total / perPage);

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <div style={s.headerLeft}>
          {['today', '7d', '30d', 'all'].map(p => (
            <button key={p} onClick={() => setPeriod(p)}
              style={{ ...s.chip, ...(period === p ? s.chipOn : {}) }}>
              {p === 'today' ? 'Hoje' : p === '7d' ? '7 dias' : p === '30d' ? '30 dias' : 'Tudo'}
            </button>
          ))}
          <span style={s.sep} />
          <button onClick={() => setNicho('')} style={{ ...s.chip, ...(!nicho ? s.chipOn : {}) }}>Todos</button>
          {nichos.map(n => (
            <button key={n} onClick={() => setNicho(n)}
              style={{ ...s.chip, ...(nicho === n ? s.chipOn : {}) }}>{n}</button>
          ))}
        </div>
        <div style={s.headerRight}>
          <span style={s.totalLabel}>{total} resultados</span>
          <select style={s.select} value={sort} onChange={e => setSort(e.target.value)}>
            <option value="mined_at">Recente</option>
            <option value="likes">Likes</option>
            <option value="comments">Comments</option>
            <option value="shares">Shares</option>
          </select>
          <input style={s.search} placeholder="Buscar..." defaultValue={search}
            onChange={e => { clearTimeout(searchTimer.current); searchTimer.current = setTimeout(() => setSearch(e.target.value), 300); }} />
        </div>
      </div>

      {/* Content */}
      {error ? (
        <div style={s.errorBox}><span>{error}</span><button style={s.retryBtn} onClick={load}>Tentar novamente</button></div>
      ) : loading ? (
        <div style={s.grid}>{[1,2,3,4,5,6].map(i => <div key={i} style={s.skeleton} />)}</div>
      ) : ads.length === 0 ? (
        <div style={s.empty}>Nenhum anuncio encontrado</div>
      ) : (
        <div style={s.grid}>
          {ads.map(ad => <AdCard key={ad.id} ad={ad} onCopy={copyUrl} onMedia={setModal} />)}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={s.paginationBar}>
          <button style={s.pageBtn} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>←</button>
          <span style={s.pageInfo}>{page} / {totalPages}</span>
          <button style={s.pageBtn} disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>→</button>
        </div>
      )}

      {toast && <div style={s.toast}>{toast}</div>}
      {modal && (
        <div style={s.modal} onClick={(e) => { if (e.target === e.currentTarget) setModal(null); }}>
          <div style={s.modalContent}>
            <button style={s.modalClose} onClick={() => setModal(null)}>✕</button>
            {modal.type === 'video' ? (
              <video src={modal.src} controls autoPlay style={s.modalMedia} />
            ) : (
              <img src={modal.src} style={s.modalMedia} alt="" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function AdCard({ ad, onCopy, onMedia }) {
  const [open, setOpen] = useState(false);
  const displayDomain = ad.display_url || (() => { try { return new URL(ad.real_url).hostname; } catch { return ''; } })();
  const pageUrl = ad.fb_page_url || (ad.ad_link && ad.ad_link.includes('facebook.com') ? ad.ad_link : '');
  const date = ad.mined_at ? new Date(ad.mined_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : '';

  const handleMedia = () => {
    onMedia(ad.video_path
      ? { type: 'video', src: ad.video_path }
      : { type: 'image', src: ad.screenshot_feed_path }
    );
  };

  return (
    <div style={s.card}>
      {/* Thumbnail */}
      <div style={s.thumbWrap} onClick={handleMedia}>
        {(ad.thumbnail_path || ad.screenshot_feed_path) ? (
          <img src={ad.thumbnail_path || ad.screenshot_feed_path} style={s.thumb}
            loading="lazy" onError={e => { e.target.style.display = 'none'; }} />
        ) : (
          <div style={s.thumbEmpty} />
        )}
        {ad.video_path && <div style={s.playIcon}>▶</div>}
        <span style={s.formatTag}>{ad.product_name || ad.format || '?'}</span>
        {ad.adlib_count > 0 && <span style={s.adlibTag}>{ad.adlib_count >= 1000 ? (ad.adlib_count/1000).toFixed(1).replace(/\.0$/,'')+'K' : ad.adlib_count} ads</span>}
      </div>

      {/* Info */}
      <div style={s.info}>
        {/* Nome + data */}
        <div style={s.row1}>
          {pageUrl ? (
            <a href={pageUrl} target="_blank" rel="noopener" style={s.pageName}>{ad.fb_page_name || '—'}</a>
          ) : (
            <span style={s.pageName}>{ad.fb_page_name || '—'}</span>
          )}
          <span style={s.date}>{date}</span>
        </div>

        {/* Dominio + nicho */}
        <div style={s.row2}>
          {displayDomain && <span style={s.domain}>{displayDomain}</span>}
          {ad.nicho && <span style={s.nicho}>{ad.nicho}</span>}
        </div>

        {/* Engagement */}
        <div style={s.row3}>
          <span>{ad.likes || 0} likes · {ad.comments || 0} comm · {ad.shares || 0} shares</span>
        </div>

        {/* Expand */}
        <button style={s.expandBtn} onClick={() => setOpen(!open)}>
          {open ? '− Fechar' : '+ Detalhes'}
        </button>

        {open && (
          <div style={s.details}>
            {ad.copy_text && <div style={s.copy}>{ad.copy_text}</div>}
            <div style={s.links}>
              {displayDomain && <LinkRow label="Exibicao" value={displayDomain} onCopy={onCopy} plain />}
              {ad.ad_link && <LinkRow label="Facebook" value={ad.ad_link} onCopy={onCopy} />}
              {ad.real_url && <LinkRow label="URL Real" value={ad.real_url} onCopy={onCopy} />}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function LinkRow({ label, value, onCopy, plain }) {
  const display = (() => { try { const u = new URL(value); return u.hostname + u.pathname.substring(0, 25); } catch { return value.substring(0, 40); } })();
  return (
    <div style={s.linkRow}>
      <span style={s.linkLabel}>{label}</span>
      {plain ? <span style={s.linkPlain}>{value}</span> : <a href={value} target="_blank" rel="noopener" style={s.linkUrl} title={value}>{display}</a>}
      <button style={s.copyBtn} onClick={() => onCopy(value)}>Copiar</button>
    </div>
  );
}

const s = {
  page: { display: 'flex', flexDirection: 'column', minHeight: '100vh' },

  // Header
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 24px', background: 'var(--surface)', borderBottom: '1px solid var(--border)', gap: 12, flexWrap: 'wrap' },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' },
  headerRight: { display: 'flex', alignItems: 'center', gap: 8 },
  totalLabel: { fontSize: 11, color: 'var(--text3)', whiteSpace: 'nowrap' },
  chip: { padding: '5px 12px', border: '1px solid var(--border)', borderRadius: 6, background: 'transparent', color: 'var(--text3)', fontSize: 11, fontWeight: 500, fontFamily: 'inherit', transition: 'all 0.15s' },
  chipOn: { background: 'var(--accent)', color: '#fff', borderColor: 'var(--accent)' },
  sep: { width: 1, height: 16, background: 'var(--border)', margin: '0 2px', display: 'inline-block' },
  select: { padding: '5px 8px', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface2)', color: 'var(--text)', fontSize: 11, fontFamily: 'inherit', outline: 'none' },
  search: { padding: '5px 12px', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface2)', color: 'var(--text)', fontSize: 11, fontFamily: 'inherit', outline: 'none', width: 140 },

  // Grid
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12, padding: 24 },
  empty: { textAlign: 'center', padding: 60, color: 'var(--text3)', fontSize: 13 },
  skeleton: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, height: 320, animation: 'pulse 1.5s ease-in-out infinite' },
  errorBox: { textAlign: 'center', padding: 40, color: 'var(--accent-r)', fontSize: 13, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 },
  retryBtn: { padding: '6px 16px', border: '1px solid var(--accent-r)', borderRadius: 6, background: 'transparent', color: 'var(--accent-r)', fontSize: 12, fontFamily: 'inherit' },

  // Card
  card: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', display: 'flex', flexDirection: 'column' },

  // Thumbnail
  thumbWrap: { position: 'relative', background: 'var(--surface3)', cursor: 'pointer', aspectRatio: '9/16', maxHeight: 280, overflow: 'hidden' },
  thumb: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  thumbEmpty: { width: '100%', height: '100%' },
  playIcon: { position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 40, height: 40, borderRadius: '50%', background: 'rgba(0,0,0,0.5)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, backdropFilter: 'blur(4px)' },
  formatTag: { position: 'absolute', top: 8, left: 8, padding: '2px 8px', borderRadius: 4, background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 },
  adlibTag: { position: 'absolute', top: 8, right: 8, padding: '2px 8px', borderRadius: 4, background: 'rgba(0,0,0,0.6)', color: 'var(--accent)', fontSize: 10, fontWeight: 600, backdropFilter: 'blur(4px)' },

  // Info
  info: { padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 5, flex: 1 },

  // Row 1: nome + data
  row1: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 },
  pageName: { fontSize: 13, fontWeight: 600, color: 'var(--text)', textDecoration: 'none', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  date: { fontSize: 11, color: 'var(--text3)', flexShrink: 0 },

  // Row 2: dominio + nicho
  row2: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 },
  domain: { color: 'var(--text2)', fontWeight: 500 },
  nicho: { color: 'var(--text3)', fontStyle: 'italic' },

  // Row 3: engagement
  row3: { fontSize: 11, color: 'var(--text3)' },

  // Expand
  expandBtn: { background: 'none', border: 'none', color: 'var(--text3)', fontSize: 11, fontFamily: 'inherit', padding: '2px 0', fontWeight: 500, textAlign: 'left' },

  // Details
  details: { display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 8, borderTop: '1px solid var(--border)' },
  copy: { fontSize: 11, color: 'var(--text2)', lineHeight: 1.5 },
  links: { display: 'flex', flexDirection: 'column', gap: 4 },
  linkRow: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 },
  linkLabel: { fontSize: 10, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', minWidth: 60, letterSpacing: 0.3 },
  linkUrl: { color: 'var(--accent)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1, textDecoration: 'none', fontSize: 11 },
  linkPlain: { color: 'var(--text2)', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 11 },
  copyBtn: { padding: '2px 8px', border: '1px solid var(--border)', borderRadius: 4, background: 'transparent', color: 'var(--text3)', fontSize: 10, fontFamily: 'inherit' },

  // Pagination
  paginationBar: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, padding: '16px' },
  pageBtn: { width: 32, height: 32, border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface)', color: 'var(--text2)', fontSize: 13, fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  pageInfo: { fontSize: 11, color: 'var(--text3)', minWidth: 50, textAlign: 'center' },

  // Modal
  modal: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modalContent: { position: 'relative', maxWidth: '90vw', maxHeight: '90vh' },
  modalClose: { position: 'absolute', top: -36, right: 0, background: 'none', border: 'none', color: '#fff', fontSize: 18, fontWeight: 700, cursor: 'pointer', padding: '4px 8px' },
  modalMedia: { maxWidth: '90vw', maxHeight: '85vh', borderRadius: 8 },

  // Toast
  toast: { position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', padding: '8px 20px', borderRadius: 8, background: 'var(--text)', color: 'var(--bg)', fontSize: 12, fontWeight: 500, zIndex: 300 },
};
